import { useEffect, useState } from 'react';
import { fetchApprovalRules, setApprovalRuleActive } from './masterRepo';
import { useMasters } from './MastersProvider';
import { useToast } from '@core/components/Toast';
import { formatYen } from '@core/lib/money';
import type { ApprovalRule, ApproverRole } from '@core/lib/types';

const APPROVER_LABELS: Record<ApproverRole, string> = {
  department_manager: '事業部責任者',
  accounting: '経理',
  admin: '管理者',
};

function conditionText(rule: ApprovalRule, departmentName: (id: string) => string): string {
  const parts: string[] = [];
  const { department_ids, account_ids, amount_min, amount_max } = rule.conditions;
  parts.push(
    amount_max === null
      ? `${formatYen(amount_min)}円以上`
      : `${formatYen(amount_min)}〜${formatYen(amount_max)}円`,
  );
  if (department_ids?.length) parts.push(`事業部: ${department_ids.map(departmentName).join('・')}`);
  if (account_ids?.length) parts.push(`科目: ${account_ids.length}件を指定`);
  return parts.join(' / ');
}

export default function ApprovalRulesTab() {
  const { departmentName } = useMasters();
  const toast = useToast();
  const [rules, setRules] = useState<ApprovalRule[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      setRules(await fetchApprovalRules());
    } catch (e) {
      toast(e instanceof Error ? e.message : '承認ルールの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(rule: ApprovalRule) {
    try {
      await setApprovalRuleActive(rule.id, !rule.active);
      await load();
      toast(rule.active ? 'ルールを無効にしました' : 'ルールを有効にしました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '更新に失敗しました');
    }
  }

  return (
    <div className="card">
      <h3>承認ルール</h3>
      <p className="hint" style={{ marginBottom: 12 }}>
        優先度(priority)の小さい順に評価し、<b>最初に条件が合った1件だけ</b>を適用します。
        「金額◯円以上は経理承認必須」のようなルールは、優先度の小さいルールを追加するだけで有効になります
        (画面からの新規追加はフェーズ2で対応。現時点ではデータ投入で追加します)。
      </p>
      {loading ? (
        <p className="empty">読み込み中…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="num">優先度</th>
              <th>ルール名</th>
              <th>条件</th>
              <th>承認ステップ</th>
              <th>状態</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td className="num">{r.priority}</td>
                <td>{r.name}</td>
                <td className="hint">{conditionText(r, departmentName)}</td>
                <td>
                  {r.steps.map((s) => (
                    <div key={s.step}>
                      {s.step}. {s.label}:{' '}
                      {s.approver_roles.map((role) => APPROVER_LABELS[role]).join(' / ')} のうち
                      {s.required_count}名
                    </div>
                  ))}
                </td>
                <td>
                  <span className={`badge ${r.active ? 'b-ok' : 'b-ng'}`}>
                    {r.active ? '有効' : '無効'}
                  </span>
                </td>
                <td>
                  <button className="txn-link" onClick={() => void toggle(r)}>
                    {r.active ? '無効にする' : '有効にする'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
